import React from "react";
import { makeStyles } from "@material-ui/styles";
import { Skeleton } from "@material-ui/lab";

const useSkeletonStyles = makeStyles({
    container: {
        padding: "16px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        marginBottom: "16px",
    },
    formLabel: {
        fontSize: "13px",
        fontWeight: 600,
        marginBottom: "8px",
        display: "block",
    },
    inputField: {
        height: "32px",
        borderRadius: "4px",
    },
    dropdownField: {
        height: "32px",
        borderRadius: "4px",
    },
    button: {
        height: "36px",
        borderRadius: "4px",
    },
    switch: {
        height: "24px",
        borderRadius: "12px",
    },
    divider: {
        height: "1px",
        margin: "16px 0",
    },
    jsonEditor: {
        height: "200px",
        borderRadius: "4px",
    },
    card: {
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        marginBottom: "16px",
    },
    listItem: {
        display: "flex",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: "1px solid #f0f0f0",
    },
    avatar: {
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        marginRight: "12px",
    },
    chip: {
        height: "24px",
        borderRadius: "12px",
    },
});

const SkeletonLoader = ({
    type = "form",
    rows = 3,
    columns = 2,
    showHeader = true,
    showActions = false,
    showPagination = false,
    taskType = null
}) => {
    const skeletonClasses = useSkeletonStyles();

    const renderFormSkeleton = () => (
        <div className={skeletonClasses.container}>
            {showHeader && (
                <div className={skeletonClasses.header}>
                    <Skeleton
                        variant="rectangular"
                        width={80}
                        height={24}
                        className={skeletonClasses.chip}
                    />
                    <Skeleton
                        variant="text"
                        width={60}
                        height={16}
                        style={{ marginLeft: "8px" }}
                    />
                </div>
            )}

            {Array.from({ length: rows }).map((_, rowIndex) => (
                <div key={rowIndex} style={{ marginBottom: "16px" }}>
                    <Skeleton
                        variant="text"
                        width={120}
                        height={16}
                        className={skeletonClasses.formLabel}
                    />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={32}
                        className={skeletonClasses.inputField}
                    />
                </div>
            ))}

            {showActions && (
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                    <Skeleton
                        variant="rectangular"
                        width={100}
                        height={36}
                        className={skeletonClasses.button}
                    />
                    <Skeleton
                        variant="rectangular"
                        width={120}
                        height={36}
                        className={skeletonClasses.button}
                    />
                </div>
            )}
        </div>
    );

    const renderTableSkeleton = () => (
        <div className={skeletonClasses.container}>
            {showHeader && (
                <div className={skeletonClasses.header}>
                    <Skeleton variant="text" width={200} height={24} />
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Skeleton
                            variant="rectangular"
                            width={80}
                            height={32}
                            className={skeletonClasses.button}
                        />
                        <Skeleton
                            variant="rectangular"
                            width={100}
                            height={32}
                            className={skeletonClasses.button}
                        />
                    </div>
                </div>
            )}

            {/* Table Header */}
            <div style={{ display: "flex", marginBottom: "8px", padding: "8px 0", borderBottom: "1px solid #e0e0e0" }}>
                {Array.from({ length: columns }).map((_, index) => (
                    <Skeleton
                        key={`header-${index}`}
                        variant="text"
                        width={index === 0 ? 60 : 100}
                        height={16}
                        style={{ marginRight: "16px" }}
                    />
                ))}
            </div>

            {/* Table Rows */}
            {Array.from({ length: rows }).map((_, rowIndex) => (
                <div key={`row-${rowIndex}`} className={skeletonClasses.listItem}>
                    {Array.from({ length: columns }).map((_, colIndex) => (
                        <Skeleton
                            key={`cell-${rowIndex}-${colIndex}`}
                            variant="text"
                            width={colIndex === 0 ? 40 : 100}
                            height={16}
                            style={{ marginRight: "16px" }}
                        />
                    ))}
                </div>
            ))}

            {showPagination && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
                    <Skeleton variant="text" width={120} height={16} />
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <Skeleton variant="rectangular" width={32} height={32} />
                        <Skeleton variant="text" width={40} height={16} />
                        <Skeleton variant="rectangular" width={32} height={32} />
                    </div>
                </div>
            )}
        </div>
    );

    const renderCardSkeleton = () => (
        <div className={skeletonClasses.card}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    className={skeletonClasses.avatar}
                />
                <div style={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="text" width="40%" height={16} />
                </div>
            </div>
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="80%" height={16} />
            <Skeleton variant="text" width="60%" height={16} />
        </div>
    );

    const renderListSkeleton = () => (
        <div className={skeletonClasses.container}>
            {Array.from({ length: rows }).map((_, index) => (
                <div key={index} className={skeletonClasses.listItem}>
                    <Skeleton
                        variant="circular"
                        width={32}
                        height={32}
                        style={{ marginRight: "12px" }}
                    />
                    <div style={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={16} />
                        <Skeleton variant="text" width="50%" height={14} />
                    </div>
                    <Skeleton
                        variant="rectangular"
                        width={60}
                        height={24}
                        className={skeletonClasses.chip}
                    />
                </div>
            ))}
        </div>
    );

    const renderTaskFormSkeleton = () => {
        // Task-specific skeleton based on task type
        const getTaskSpecificFields = () => {
            switch (taskType) {
                case "HTTP":
                case "HTTP_POLL":
                    return (
                        <>
                            <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                                <div style={{ flex: 1 }}>
                                    <Skeleton variant="text" width={40} height={16} className={skeletonClasses.formLabel} />
                                    <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.inputField} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Skeleton variant="text" width={60} height={16} className={skeletonClasses.formLabel} />
                                    <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.dropdownField} />
                                </div>
                            </div>
                            <div style={{ marginBottom: "16px" }}>
                                <Skeleton variant="text" width={80} height={16} className={skeletonClasses.formLabel} />
                                <Skeleton variant="rectangular" width="100%" height={200} className={skeletonClasses.jsonEditor} />
                            </div>
                        </>
                    );
                case "HUMAN":
                    return (
                        <>
                            <div style={{ marginBottom: "16px" }}>
                                <Skeleton variant="text" width={120} height={16} className={skeletonClasses.formLabel} />
                                <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.dropdownField} />
                            </div>
                            <div style={{ marginBottom: "16px" }}>
                                <Skeleton variant="text" width={100} height={16} className={skeletonClasses.formLabel} />
                                <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.dropdownField} />
                            </div>
                        </>
                    );
                default:
                    return (
                        <>
                            <div style={{ marginBottom: "16px" }}>
                                <Skeleton variant="text" width={100} height={16} className={skeletonClasses.formLabel} />
                                <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.inputField} />
                            </div>
                        </>
                    );
            }
        };

        return (
            <div className={skeletonClasses.container}>
                {showHeader && (
                    <div className={skeletonClasses.header}>
                        <Skeleton
                            variant="rectangular"
                            width={80}
                            height={24}
                            className={skeletonClasses.chip}
                        />
                        <Skeleton
                            variant="text"
                            width={60}
                            height={16}
                            style={{ marginLeft: "8px" }}
                        />
                    </div>
                )}

                {/* Common fields */}
                <div style={{ marginBottom: "16px" }}>
                    <Skeleton variant="text" width={60} height={16} className={skeletonClasses.formLabel} />
                    <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.inputField} />
                </div>

                <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                    <div style={{ flex: 1 }}>
                        <Skeleton variant="text" width={100} height={16} className={skeletonClasses.formLabel} />
                        <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.inputField} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Skeleton variant="text" width={120} height={16} className={skeletonClasses.formLabel} />
                        <Skeleton variant="rectangular" width="100%" height={32} className={skeletonClasses.inputField} />
                    </div>
                </div>

                {/* Task-specific fields */}
                {getTaskSpecificFields()}

                {/* Common bottom elements */}
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={1}
                    className={skeletonClasses.divider}
                />

                <div style={{ display: "flex", alignItems: "center", marginTop: "16px" }}>
                    <Skeleton
                        variant="rectangular"
                        width={48}
                        height={24}
                        className={skeletonClasses.switch}
                    />
                    <Skeleton
                        variant="text"
                        width={80}
                        height={16}
                        style={{ marginLeft: "8px" }}
                    />
                </div>

                {showActions && (
                    <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                        <Skeleton
                            variant="rectangular"
                            width={100}
                            height={36}
                            className={skeletonClasses.button}
                        />
                        <Skeleton
                            variant="rectangular"
                            width={120}
                            height={36}
                            className={skeletonClasses.button}
                        />
                    </div>
                )}
            </div>
        );
    };

    switch (type) {
        case "form":
            return renderFormSkeleton();
        case "table":
            return renderTableSkeleton();
        case "card":
            return renderCardSkeleton();
        case "list":
            return renderListSkeleton();
        case "task":
            return renderTaskFormSkeleton();
        default:
            return renderFormSkeleton();
    }
};

export default SkeletonLoader;
